import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { selectedBranch } from '@ubeya/shared/atoms/shared';
import useEmployeeTimeAvailabilities from '@ubeya/shared/hooks/account/useEmployeeTimeAvailabilities';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { Button, Modal as BaseModal } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { schedulingTimeRange } from 'shared/atoms/scheduling';
import EmployeeTimeAvailability from './EmployeeTimeAvailability';

const Modal = styled(BaseModal)`
  .summary {
    margin-right: auto;
  }

  .buttons {
    padding: 10px;
  }
`;

const Text = styled.div.attrs(() => ({ className: 'summary' }))`
  color: ${({ theme }) => theme.colors.black};
  margin-left: 20px;
  font-size: 13px;
`;

const EmployeeTimeAvailabilityModal = ({ onClose, accountId, employee, refetchOnAvailabilityChange }) => {
  const { t } = useContext(ConfigContext);
  const timeRange = useRecoilValue(schedulingTimeRange);
  const selectedBranchId = useRecoilValue(selectedBranch);

  const { employeeId, firstName = '', lastName = '' } = employee || {};

  const { timeAvailabilities } = useEmployeeTimeAvailabilities({
    accountId,
    employeeId,
    from: moment(timeRange.startDate).format(API_DATE_FORMAT),
    to: moment(timeRange.endDate).format(API_DATE_FORMAT),
    refetchOnAvailabilityChange
  });

  const totalHours = useMemo(
    () =>
      (timeAvailabilities || []).reduce(
        (acc, curr) => acc + moment.duration(moment(curr.endTime).diff(moment(curr.startTime))).asHours(),
        0
      ),
    [timeAvailabilities]
  );

  const employeeName = useMemo(() => `${firstName} ${lastName}`, [firstName, lastName]);

  const title = useMemo(
    () =>
      `${employeeName}, ${moment(timeRange.startDate).format('DD MMM')} - ${moment(timeRange.endDate).format(
        'DD MMM'
      )}`,
    [employeeName, timeRange.endDate, timeRange.startDate]
  );

  return (
    <Modal
      usePortal
      size="long"
      onClose={onClose}
      showConfirmButton={false}
      isPaddingContent={false}
      closeOnClickOutside
      renderButtons={() => (
        <>
          {totalHours !== 0 && <Text>{t('timeAvailabilityWeekSummarize', { hours: totalHours.toFixed(1) })}</Text>}
          <Button primary onClick={onClose} paddingVertical={0}>
            {t('done')}
          </Button>
        </>
      )}
      title={title}>
      <EmployeeTimeAvailability
        selectedBranchId={selectedBranchId}
        accountId={accountId}
        employee={employee}
        refetchOnAvailabilityChange={refetchOnAvailabilityChange}
        timeRange={timeRange}
      />
    </Modal>
  );
};

export default EmployeeTimeAvailabilityModal;
